/**
 * Number formatting for everything the engine produces.
 *
 * Deliberately not `Intl`: these strings appear in the table, on chart axes, in
 * exported documents and in server-rendered summaries, and they must be
 * byte-identical in all three. `Intl` output varies with the ICU build and the
 * host locale, which would make a golden-file test a lottery. The grouping rule
 * here is the `en-US` one the ZNMS page already used.
 */

/**
 * U+2212 MINUS SIGN, not a hyphen: it is the width of a digit, so a column of
 * figures with tabular numerals stays aligned.
 */
export const MINUS = "−";

/** Symbols for the currencies this shop quotes in; anything else prints its code. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  INR: "₹",
  AUD: "A$",
  CAD: "C$",
  NZD: "NZ$",
  SGD: "S$",
  CNY: "CN¥",
  KRW: "₩",
  THB: "฿",
  PHP: "₱",
};

export function currencySymbol(currencyCode: string): string {
  const code = currencyCode.trim().toUpperCase();
  return CURRENCY_SYMBOLS[code] ?? code;
}

/** Thousands separators, en-US style, on an already-rounded absolute value. */
function group(digits: string): string {
  let out = "";
  for (let i = 0; i < digits.length; i += 1) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ",";
    out += digits[i];
  }
  return out;
}

/** Drop a trailing ".0" so "1.0M" reads as "1M". */
function trimTenth(text: string): string {
  return text.endsWith(".0") ? text.slice(0, -2) : text;
}

export interface MoneyOptions {
  /** Abbreviate large figures: 1.2M, 340K. For axis labels and chart tooltips. */
  compact?: boolean;
  /** Keep two decimal places instead of rounding to whole units. */
  cents?: boolean;
  /** Prefix a non-negative figure with "+". For deltas on the compare screen. */
  signed?: boolean;
}

export function formatMoney(
  currencyCode: string,
  value: number,
  options: MoneyOptions = {},
): string {
  if (!Number.isFinite(value)) return `${currencySymbol(currencyCode)} —`;

  const symbol = currencySymbol(currencyCode);
  const abs = Math.abs(value);
  const sign = value < 0 ? MINUS : options.signed && value > 0 ? "+" : "";

  let body: string;
  if (options.compact && abs >= 1_000_000) {
    body = `${trimTenth((abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1))}M`;
  } else if (options.compact && abs >= 10_000) {
    body = `${trimTenth((abs / 1000).toFixed(abs >= 100_000 ? 0 : 1))}K`;
  } else if (options.cents) {
    const fixed = abs.toFixed(2);
    const dot = fixed.indexOf(".");
    body = `${group(fixed.slice(0, dot))}${fixed.slice(dot)}`;
  } else {
    body = group(String(Math.round(abs)));
  }

  // A multi-character symbol is a word — "MVR 1,000" — where "$1,000" is not.
  const spacer = symbol.length > 1 ? " " : "";
  return `${sign}${symbol}${spacer}${body}`;
}

/** A whole count with separators: client and unit columns. */
export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const rounded = Math.round(Math.abs(value));
  return `${value < 0 ? MINUS : ""}${group(String(rounded))}`;
}

export function formatPercent(value: number, decimals = 1): string {
  if (!Number.isFinite(value)) return "—";
  const abs = Math.abs(value).toFixed(decimals);
  return `${value < 0 ? MINUS : ""}${abs}%`;
}

/**
 * "3.2 yrs", "Immediate", or "Not in 5 yrs" — the same three answers the ZNMS
 * page gives, because "never, within the horizon you chose" is the useful
 * statement and a blank cell is not.
 */
export function formatBreakEven(years: number | null, horizonYears: number): string {
  if (years === null) return `Not in ${horizonYears} yrs`;
  if (years <= 0) return "Immediate";
  return `${years.toFixed(1)} yrs`;
}

/** The multiplier a package's recurring price is quoted in, for field labels. */
export function periodLabel(period: "MONTHLY" | "QUARTERLY" | "ANNUAL"): string {
  return period === "MONTHLY" ? "per month" : period === "QUARTERLY" ? "per quarter" : "per year";
}
